import { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, ArrowUpRight, Download, X } from "lucide-react";

interface InvalidAaxFileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const audibleStoreLinks = [
  {
    label: "United States",
    code: "US",
    url: "https://www.audible.com/library/titles",
  },
  {
    label: "Germany",
    code: "DE",
    url: "https://www.audible.de/library/titles",
  },
  {
    label: "Canada",
    code: "CA",
    url: "https://www.audible.ca/library/titles",
  },
  {
    label: "Brazil",
    code: "BR",
    url: "https://www.audible.com.br/library/titles",
  },
  {
    label: "United Kingdom",
    code: "UK",
    url: "https://www.audible.co.uk/library/titles",
  },
  {
    label: "France",
    code: "FR",
    url: "https://www.audible.fr/library/titles",
  },
  {
    label: "Italy",
    code: "IT",
    url: "https://www.audible.it/library/titles",
  },
  {
    label: "Spain",
    code: "ES",
    url: "https://www.audible.es/library/titles",
  },
  {
    label: "Australia",
    code: "AU",
    url: "https://www.audible.com.au/library/titles",
  },
  {
    label: "India",
    code: "IN",
    url: "https://www.audible.in/library/titles",
  },
  {
    label: "Japan",
    code: "JP",
    url: "https://www.audible.co.jp/library/titles",
  },
];

type AudibleStoreLink = (typeof audibleStoreLinks)[number];

const localeRegionToStoreCode: Record<string, string> = {
  AU: "AU",
  BR: "BR",
  CA: "CA",
  DE: "DE",
  ES: "ES",
  FR: "FR",
  GB: "UK",
  IN: "IN",
  IT: "IT",
  JP: "JP",
  UK: "UK",
  US: "US",
};

const localeLanguageToStoreCode: Record<string, string> = {
  de: "DE",
  en: "US",
  es: "ES",
  fr: "FR",
  hi: "IN",
  it: "IT",
  ja: "JP",
  pt: "BR",
};

function inferPreferredAudibleStore(): AudibleStoreLink {
  if (typeof navigator === "undefined") {
    return audibleStoreLinks[0];
  }

  const localeCandidates = [
    ...(navigator.languages || []),
    navigator.language,
    Intl.DateTimeFormat().resolvedOptions().locale,
  ].filter(Boolean);

  for (const locale of localeCandidates) {
    const normalizedLocale = locale.replace("_", "-");
    const [, region] = normalizedLocale.split("-");

    if (region) {
      const regionMatch = audibleStoreLinks.find(
        (store) => store.code === localeRegionToStoreCode[region.toUpperCase()]
      );

      if (regionMatch) {
        return regionMatch;
      }
    }

    const language = normalizedLocale.split("-")[0]?.toLowerCase();
    if (language) {
      const languageMatch = audibleStoreLinks.find(
        (store) => store.code === localeLanguageToStoreCode[language]
      );

      if (languageMatch) {
        return languageMatch;
      }
    }
  }

  return audibleStoreLinks[0];
}

function StoreLinkCard({
  label,
  code,
  url,
  isPreferred = false,
}: AudibleStoreLink & { isPreferred?: boolean }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={`group flex min-h-14 items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 ${
        isPreferred
          ? "border-blue-300 bg-blue-50 shadow-sm hover:border-blue-400 hover:bg-blue-100/80 dark:border-blue-500/60 dark:bg-blue-500/10 dark:hover:bg-blue-500/15"
          : "border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/70 dark:border-slate-700 dark:bg-slate-900/80 dark:hover:border-blue-500 dark:hover:bg-slate-800"
      }`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-100 via-amber-50 to-blue-100 text-xs font-bold tracking-[0.18em] text-slate-700 dark:from-orange-500/20 dark:via-amber-500/10 dark:to-blue-500/20 dark:text-slate-200">
          {code}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-slate-900 dark:text-white">
              {label}
            </div>
            {isPreferred && (
              <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white">
                Recommended
              </span>
            )}
          </div>
          <div className="truncate text-xs text-slate-500 dark:text-slate-400">
            {url.replace("https://", "")}
          </div>
        </div>
      </div>
      <ArrowUpRight
        className="h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-blue-600 dark:group-hover:text-blue-400"
        aria-hidden="true"
      />
    </a>
  );
}

export function InvalidAaxFileModal({
  isOpen,
  onClose,
}: InvalidAaxFileModalProps) {
  const preferredStore = useMemo(() => inferPreferredAudibleStore(), []);
  const prioritizedStores = useMemo(() => {
    return [
      preferredStore,
      ...audibleStoreLinks.filter((store) => store.url !== preferredStore.url),
    ];
  }, [preferredStore]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm"
            onClick={onClose}
            role="presentation"
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, y: 48, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 32, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="relative z-50 flex max-h-[calc(100dvh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl dark:border-slate-700 dark:bg-slate-900 sm:max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="invalid-aax-title"
            aria-describedby="invalid-aax-description"
          >
            <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-r from-amber-200/60 via-orange-100/60 to-blue-100/60 blur-2xl dark:from-amber-500/10 dark:via-orange-500/10 dark:to-blue-500/10" />

            <div className="relative shrink-0 border-b border-slate-200/80 px-5 py-4 dark:border-slate-700 sm:px-6">
              <div className="flex items-center justify-between gap-4">
                <div className="inline-flex items-center rounded-full border border-amber-200 bg-white/80 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-amber-700 dark:border-amber-500/20 dark:bg-slate-800/80 dark:text-amber-300">
                  Unsupported File Source
                </div>
                <button
                  onClick={onClose}
                  className="rounded-full p-2 text-slate-500 transition-colors duration-200 hover:bg-slate-200 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                  aria-label="Close invalid aax file warning"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="relative min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
              <div className="mb-5 flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 shadow-sm dark:bg-amber-500/15 dark:text-amber-300">
                  <AlertTriangle className="h-6 w-6" aria-hidden="true" />
                </div>
                <div>
                  <h2
                    id="invalid-aax-title"
                    className="text-2xl font-semibold tracking-tight text-slate-950 dark:text-white"
                  >
                    Invalid aax file
                  </h2>
                  <p
                    id="invalid-aax-description"
                    className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300"
                  >
                    This audiobook file appears to come from unsupported third-party
                    software. Audible Tools only works with original `.aax` downloads
                    from your Audible library.
                  </p>
                </div>
              </div>

              <div className="mb-5 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
                  <p className="text-sm leading-6 text-slate-700 dark:text-slate-200">
                    Please download the aax file directly from your Audible account.
                    Audiobooks downloaded by third-party software like "OpenAudible"
                    are not supported.
                  </p>
                </div>
                <a
                  href={preferredStore.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-14 items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                  Open {preferredStore.label} Library
                </a>
              </div>

              <div className="mb-4 flex items-end justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    Choose Your Marketplace
                  </h3>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    {preferredStore.label} looks like the best match for this device.
                    You can still choose a different Audible marketplace below.
                  </p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {prioritizedStores.map((store) => (
                  <StoreLinkCard
                    key={store.url}
                    {...store}
                    isPreferred={store.url === preferredStore.url}
                  />
                ))}
              </div>
            </div>

            <div className="shrink-0 border-t border-slate-200/80 bg-white/80 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/80 sm:px-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Download the original `.aax` file, then add it here again.
                </p>
                <button
                  onClick={onClose}
                  className="inline-flex min-h-11 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors duration-200 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
